
import React, { JSX, useEffect, useLayoutEffect, useRef, useState } from 'react'

export const SCORE_ROLL_MS = 350

interface RollSpec {
  id: number
  top: number
  bottom: number
  fromY: number
  toY: number
}

const RollStack = ({
  spec,
  lineHeight,
  rollMs
}: {
  spec: RollSpec
  lineHeight: number
  rollMs: number
}): JSX.Element => {
  const [y, setY] = useState(spec.fromY)
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setY(spec.toY))
    return () => cancelAnimationFrame(raf)
  }, [spec.toY])
  return (
    <span
      style={{
        display: 'inline-block',
        height: `${lineHeight}px`,
        overflow: 'hidden',
        verticalAlign: 'bottom',
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      <span
        style={{
          display: 'block',
          transform: `translateY(${y}px)`,
          transition: `transform ${rollMs}ms ease-out`
        }}
      >
        <span style={{ display: 'block', height: `${lineHeight}px` }}>{spec.top}</span>
        <span style={{ display: 'block', height: `${lineHeight}px` }}>{spec.bottom}</span>
      </span>
    </span>
  )
}

export const RollingNumber = ({
  value,
  lineHeight,
  rollMs = SCORE_ROLL_MS,
  direction = 'auto'
}: {
  value: number
  lineHeight: number
  rollMs?: number
  direction?: 'auto' | 'up'
}): JSX.Element => {
  const [settled, setSettled] = useState(value)
  const [roll, setRoll] = useState<RollSpec | null>(null)
  const prevRef = useRef(value)
  const idRef = useRef(0)

  useEffect(() => {
    if (value === prevRef.current) return
    const up = direction === 'up' ? true : value > prevRef.current
    const old = prevRef.current
    prevRef.current = value
    idRef.current += 1
    setRoll({
      id: idRef.current,
      top: up ? old : value,
      bottom: up ? value : old,
      fromY: up ? 0 : -lineHeight,
      toY: up ? -lineHeight : 0
    })
    const t = globalThis.setTimeout(() => {
      setSettled(value)
      setRoll(null)
    }, rollMs)
    return () => globalThis.clearTimeout(t)
  }, [value, lineHeight, rollMs])

  if (roll === null) return <>{settled}</>
  return <RollStack key={roll.id} spec={roll} lineHeight={lineHeight} rollMs={rollMs} />
}
